import moment, { Moment as TMoment } from 'moment';
import React, { Component, MouseEvent } from 'react';

import { ChevronLeft, ChevronRight } from 'assets/icons/Icons';
import { CalendarDateProps } from 'components/Calendar';
import { TimeSlot, themes } from 'kb-shared';
import { momentFormatted, momentInTimeZone } from 'kb-shared/utilities/momentHelpers';
import styled from 'styled-components';

interface ArrowButtonProps {
  $isActive?: boolean;
}

const Container = styled.div`
  width: 340px;
  min-width: 340px;
  border: 1px solid ${themes.colors.neutral.lavender};
  border-radius: 8px;
  margin-bottom: 20px;
  padding: 16px;
  background: white;
`;

const CalendarHeader = styled.h2`
  font-size: 1.25em;
  font-weight: 600;
  color: ${themes.colors.neutral.primaryNavy};
  margin: 0;
`;

const Navigation = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  height: 40px;
  width: 100%;
  margin-bottom: 8px;
`;

const ArrowButton = styled.button.attrs<ArrowButtonProps>({
  type: 'button'
})<ArrowButtonProps>`
  cursor: ${({ $isActive = true }) => ($isActive ? 'pointer' : 'default')};
  padding: 8px;
  border: none;
  background: transparent;
  color: ${({ $isActive = true }) =>
    $isActive ? themes.colors.neutral.primaryNavy : themes.colors.neutral.lavender};
  opacity: ${({ $isActive = true }) => ($isActive ? 1 : 0.5)};
  display: flex;
  align-items: center;
  justify-content: center;

  &:focus {
    outline: none;
  }

  &:hover {
    opacity: ${({ $isActive = true }) => ($isActive ? 0.7 : 0.5)};
  }
`;

const DatesWrapper = styled.div.attrs<{ 'data-testid'?: string }>({
  'data-testid': 'calendar-dates-container'
})`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const RowWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
`;

interface BookingCalendarDateProps extends CalendarDateProps {
  $isAdjacentMonth?: boolean;
}

const DateCell = styled.button.attrs<BookingCalendarDateProps>({
  type: 'button'
})<BookingCalendarDateProps>`
  cursor: ${props => (props.disabled ? 'default' : 'pointer')};
  flex: 1;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: ${props => (props.selected ? themes.colors.yellow.primary : 'transparent')};
  border-radius: ${props => (props.selected ? '8px' : '0')};
  color: ${props => {
    if (props.$isAdjacentMonth) return themes.colors.neutral.lavender;
    if (props.disabled) return themes.colors.neutral.lavender;
    return themes.colors.neutral.primaryNavy;
  }};
  font-size: 14px;
  font-weight: ${props => (props.selected ? '600' : '400')};

  &:focus {
    outline: none;
  }
`;

const DayOfWeek = styled.div`
  flex: 1;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 500;
  color: ${themes.colors.neutral.navy};
`;

export type CalendarCell = {
  isDate?: boolean;
  isSelected?: boolean;
  isToday?: boolean;
  isAdjacentMonth?: boolean;
  value: string;
  date?: TMoment;
};

export function getCalendarCell(
  value: string,
  isDate?: boolean,
  isSelected?: boolean,
  isToday?: boolean,
  date?: TMoment,
  isAdjacentMonth?: boolean
): CalendarCell {
  return {
    value,
    isDate,
    isSelected,
    isToday,
    date,
    isAdjacentMonth
  };
}

export function getCalendarRows(
  currentMonth: TMoment,
  selectedDate: TMoment | null,
  currentDayCanBeSelected: boolean | undefined
): Array<Array<CalendarCell>> {
  const daysInMonth = currentMonth.daysInMonth();
  // Get first day of month - convert Sunday=0 to Monday=0 format
  const firstDayOfMonth = currentMonth.clone().startOf('month').day();
  // Convert to Monday-start (Monday=0, Sunday=6)
  const firstWeekdayOfMonth = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;
  const today = moment();

  const rowsInMonth = Math.ceil((daysInMonth + firstWeekdayOfMonth) / 7);
  const totalCells = rowsInMonth * 7;
  const calendarCells: CalendarCell[] = [];

  const prevMonth = currentMonth.clone().subtract(1, 'month');
  const daysInPrevMonth = prevMonth.daysInMonth();
  for (let i = 0; i < firstWeekdayOfMonth; i++) {
    const day = daysInPrevMonth - firstWeekdayOfMonth + i + 1;
    const cellDate = prevMonth.clone().date(day);
    calendarCells.push(getCalendarCell(day.toString(), true, false, false, cellDate, true));
  }

  for (let dayOfMonth = 1; dayOfMonth <= daysInMonth; dayOfMonth++) {
    const isToday =
      today.date() === dayOfMonth &&
      today.month() === currentMonth.month() &&
      today.year() === currentMonth.year();
    const cellDate = currentMonth.clone().date(dayOfMonth);
    let isSelected =
      selectedDate &&
      currentMonth.month() === selectedDate.month() &&
      dayOfMonth === selectedDate.tz(moment.tz.guess()).date() &&
      currentMonth.year() === selectedDate.year();

    if (
      isSelected === true &&
      selectedDate?.date() === today.date() &&
      selectedDate?.month() === today.month() &&
      !currentDayCanBeSelected
    ) {
      isSelected = false;
      selectedDate.add(1, 'days');
    }
    if (!isSelected) isSelected = false;

    calendarCells.push(
      getCalendarCell(dayOfMonth.toString(), true, isSelected, isToday, cellDate, false)
    );
  }

  const nextMonth = currentMonth.clone().add(1, 'month');
  const remainingCells = totalCells - calendarCells.length;
  for (let i = 1; i <= remainingCells; i++) {
    const cellDate = nextMonth.clone().date(i);
    calendarCells.push(getCalendarCell(i.toString(), true, false, false, cellDate, true));
  }

  const calendarRows: CalendarCell[][] = [];
  for (let i = 0; i < rowsInMonth; i++) {
    calendarRows.push(calendarCells.slice(i * 7, (i + 1) * 7));
  }

  return calendarRows;
}

type Props = {
  selectedDate: TMoment | null;
  currentMonth: TMoment;
  onPrevious: (event: MouseEvent) => void;
  onNext: (event: MouseEvent) => void;
  onSelectDate?: (date: TMoment) => void;
  renderBorder?: boolean;
  timeSlotsResults: Array<TimeSlot>;
  currentDayCanBeSelected?: boolean;
};

type State = {
  today: TMoment;
  yesterday: TMoment;
};

export default class BookingCalendar extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      today: moment(),
      yesterday: moment().add(-1, 'days')
    };
  }

  selectDate(day: number) {
    const { onSelectDate } = this.props;
    const date = this.props.currentMonth.clone().date(day);
    if (!onSelectDate) return;
    onSelectDate(date);
  }

  enableArrowDateComparison = (currentDate: TMoment, dateComparison: TMoment) => {
    const currentMonth = currentDate.month();
    const currentYear = currentDate.year();

    const comparisonMonth = dateComparison.month();
    const comparisonYear = dateComparison.year();

    return (
      currentYear < comparisonYear ||
      (currentYear === comparisonYear && currentMonth < comparisonMonth)
    );
  };

  findAvailableDate = (date: TMoment | undefined, timeSlots: Array<TimeSlot>) =>
    !!timeSlots.find(t => date?.date() === momentInTimeZone(t.startTime, t.timeZone).date());

  render() {
    const { today } = this.state;
    const { currentMonth, timeSlotsResults } = this.props;
    const yesterday = today.subtract(1, 'days');
    const activeArrow = this.enableArrowDateComparison(today, currentMonth);

    return (
      <Container>
        <Navigation>
          <ArrowButton
            disabled={!activeArrow}
            $isActive={activeArrow}
            onClick={this.props.onPrevious}
            aria-label="Previous month"
            data-testid="button-prev-month"
          >
            <ChevronLeft type="solid" />
          </ArrowButton>
          <CalendarHeader>{this.props.currentMonth.format('MMMM YYYY')}</CalendarHeader>
          <ArrowButton
            $isActive={true}
            onClick={this.props.onNext}
            aria-label="Next month"
            data-testid="button-next-month"
          >
            <ChevronRight type="solid" />
          </ArrowButton>
        </Navigation>
        <DatesWrapper>
          <RowWrapper>
            <DayOfWeek>Mo</DayOfWeek>
            <DayOfWeek>Tu</DayOfWeek>
            <DayOfWeek>We</DayOfWeek>
            <DayOfWeek>Th</DayOfWeek>
            <DayOfWeek>Fr</DayOfWeek>
            <DayOfWeek>Sa</DayOfWeek>
            <DayOfWeek>Su</DayOfWeek>
          </RowWrapper>
          {getCalendarRows(
            moment(this.props.currentMonth),
            this.props.selectedDate,
            this.props.currentDayCanBeSelected
          ).map((row: CalendarCell[], index: number) => {
            const month = this.props.currentMonth.month();
            return (
              <RowWrapper key={`row-${month}-${index}`}>
                {row.map((cell: CalendarCell, cellIndex: number) => {
                  const isDisabled =
                    cell.isAdjacentMonth ||
                    !cell.date ||
                    yesterday > cell.date ||
                    today.endOf('day') > cell.date ||
                    !this.findAvailableDate(cell.date, timeSlotsResults);
                  return (
                    <DateCell
                      key={`date-${month}-${cellIndex}`}
                      selected={cell.isSelected}
                      $isAdjacentMonth={cell.isAdjacentMonth}
                      onClick={
                        !isDisabled ? this.selectDate.bind(this, parseInt(cell.value)) : () => {}
                      }
                      disabled={isDisabled}
                      aria-hidden={!cell.date}
                      aria-selected={cell.isSelected}
                      aria-label={
                        cell.date && momentFormatted(cell.date.toString(), 'MMM DD, YYYY')
                      }
                    >
                      {cell.value}
                    </DateCell>
                  );
                })}
              </RowWrapper>
            );
          })}
        </DatesWrapper>
      </Container>
    );
  }
}
